import { AIRPORTS, findAirport } from '@/data/airports';
import { motion } from 'framer-motion';
import {
  Cloud,
  CloudLightning,
  Minus,
  Move,
  Plus,
  RotateCcw,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

const REFERENCE_WIDTH = 1920;
const REFERENCE_HEIGHT = 1080;
const MIN_ZOOM = 1;
const MAX_ZOOM = 3.2;
const ZOOM_STEP = 0.18;
const DOUBLE_CLICK_ZOOM = 1.35;
const PAN_PADDING = 120;
const INITIAL_VIEW = { scale: 1, x: 0, y: 0 };

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const scalePixelCoords = (
  px,
  py,
  displayedWidth,
  displayedHeight,
  offsetX = 0,
  offsetY = 0
) => {
  const scaleX = displayedWidth / REFERENCE_WIDTH;
  const scaleY = displayedHeight / REFERENCE_HEIGHT;

  return {
    x: px * scaleX + offsetX,
    y: py * scaleY + offsetY
  };
};

const getRouteCurve = (from, to) => {
  const midX = (from.x + to.x) / 2;
  const midY = (from.y + to.y) / 2;
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const dist = Math.sqrt(dx * dx + dy * dy) || 1;
  const curve = dist * 0.2;

  return {
    controlX: midX - (dy / dist) * curve,
    controlY: midY + (dx / dist) * curve,
  };
};

export const EuropeMap = ({ scenario, activeRoutes = [], weatherEvents = [] }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const dragStateRef = useRef({
    active: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
  });

  const [dimensions, setDimensions] = useState({
    width: 800,
    height: 600,
    offsetX: 0,
    offsetY: 0,
    containerWidth: 800,
    containerHeight: 600
  });
  const [view, setView] = useState(INITIAL_VIEW);
  const [hoveredAirport, setHoveredAirport] = useState(null);
  const [hoveredRoute, setHoveredRoute] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const constrainView = useCallback((nextView) => {
    const { containerWidth, containerHeight } = dimensions;
    const scaledWidth = containerWidth * nextView.scale;
    const scaledHeight = containerHeight * nextView.scale;

    const minX = scaledWidth <= containerWidth + PAN_PADDING * 2
      ? -PAN_PADDING
      : containerWidth - scaledWidth - PAN_PADDING;
    const maxX = scaledWidth <= containerWidth + PAN_PADDING * 2
      ? PAN_PADDING
      : PAN_PADDING;

    const minY = scaledHeight <= containerHeight + PAN_PADDING * 2
      ? -PAN_PADDING
      : containerHeight - scaledHeight - PAN_PADDING;
    const maxY = scaledHeight <= containerHeight + PAN_PADDING * 2
      ? PAN_PADDING
      : PAN_PADDING;

    return {
      scale: clamp(nextView.scale, MIN_ZOOM, MAX_ZOOM),
      x: clamp(nextView.x, minX, maxX),
      y: clamp(nextView.y, minY, maxY),
    };
  }, [dimensions]);

  const projectPoint = useCallback((pointX, pointY, customView = view) => ({
    x: pointX * customView.scale + customView.x,
    y: pointY * customView.scale + customView.y,
  }), [view]);

  const unprojectPoint = useCallback((pointX, pointY, customView = view) => ({
    x: (pointX - customView.x) / customView.scale,
    y: (pointY - customView.y) / customView.scale,
  }), [view]);

  const applyZoom = useCallback((targetScale, originX, originY) => {
    setView((prev) => {
      const clampedScale = clamp(targetScale, MIN_ZOOM, MAX_ZOOM);
      const worldPoint = unprojectPoint(originX, originY, prev);

      return constrainView({
        scale: clampedScale,
        x: originX - worldPoint.x * clampedScale,
        y: originY - worldPoint.y * clampedScale,
      });
    });
  }, [constrainView, unprojectPoint]);

  const resetView = useCallback(() => {
    setView(INITIAL_VIEW);
  }, []);

  useEffect(() => {
    resetView();
    setHoveredAirport(null);
    setHoveredRoute(null);
  }, [scenario?.id, resetView]);

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current && imgRef.current) {
        const container = containerRef.current;
        const img = imgRef.current;
        const containerRect = container.getBoundingClientRect();
        const imgNaturalWidth = img.naturalWidth || REFERENCE_WIDTH;
        const imgNaturalHeight = img.naturalHeight || REFERENCE_HEIGHT;
        const imgAspect = imgNaturalWidth / imgNaturalHeight;
        const containerAspect = containerRect.width / containerRect.height;

        let displayedWidth;
        let displayedHeight;
        let offsetX = 0;
        let offsetY = 0;

        if (imgAspect > containerAspect) {
          displayedWidth = containerRect.width;
          displayedHeight = displayedWidth / imgAspect;
          offsetY = (containerRect.height - displayedHeight) / 2;
        } else {
          displayedHeight = containerRect.height;
          displayedWidth = displayedHeight * imgAspect;
          offsetX = (containerRect.width - displayedWidth) / 2;
        }

        setDimensions({
          width: displayedWidth,
          height: displayedHeight,
          offsetX,
          offsetY,
          containerWidth: containerRect.width,
          containerHeight: containerRect.height
        });
      } else if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({
          width: rect.width,
          height: rect.height,
          offsetX: 0,
          offsetY: 0,
          containerWidth: rect.width,
          containerHeight: rect.height
        });
      }
    };

    updateDimensions();

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(updateDimensions);
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    window.addEventListener('resize', updateDimensions);

    const img = imgRef.current;
    if (img) {
      if (img.complete) {
        updateDimensions();
      } else {
        img.addEventListener('load', updateDimensions);
      }
    }

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateDimensions);
      if (img) {
        img.removeEventListener('load', updateDimensions);
      }
    };
  }, []);

  useEffect(() => {
    setView((prev) => constrainView(prev));
  }, [dimensions.containerWidth, dimensions.containerHeight, constrainView]);

  const drawRoutes = useCallback((ctx, nextDimensions, routes, hoveredRouteIndex) => {
    const { width, height, offsetX, offsetY } = nextDimensions;

    routes.forEach((route, index) => {
      const fromAirport = findAirport(route.from);
      const toAirport = findAirport(route.to);

      if (!fromAirport || !toAirport || !fromAirport.px || !toAirport.px) return;

      const fromBase = scalePixelCoords(fromAirport.px, fromAirport.py, width, height, offsetX, offsetY);
      const toBase = scalePixelCoords(toAirport.px, toAirport.py, width, height, offsetX, offsetY);
      const from = projectPoint(fromBase.x, fromBase.y);
      const to = projectPoint(toBase.x, toBase.y);
      const isHovered = hoveredRouteIndex === index;

      let strokeColor;
      switch (route.status) {
        case 'critical':
        case 'cancelled':
          strokeColor = isHovered ? 'rgba(239, 68, 68, 0.9)' : 'rgba(239, 68, 68, 0.6)';
          break;
        case 'delayed':
          strokeColor = isHovered ? 'rgba(245, 158, 11, 0.9)' : 'rgba(245, 158, 11, 0.6)';
          break;
        case 'normal':
        default:
          strokeColor = isHovered ? 'rgba(16, 185, 129, 0.8)' : 'rgba(16, 185, 129, 0.5)';
      }

      const { controlX, controlY } = getRouteCurve(from, to);

      ctx.save();
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = isHovered ? 4 : 2;
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(from.x, from.y);
      ctx.quadraticCurveTo(controlX, controlY, to.x, to.y);
      ctx.stroke();

      if (isHovered) {
        ctx.shadowColor = strokeColor;
        ctx.shadowBlur = 12;
        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        ctx.quadraticCurveTo(controlX, controlY, to.x, to.y);
        ctx.stroke();
        ctx.shadowBlur = 0;
      }

      if (route.status === 'critical' || route.status === 'cancelled') {
        ctx.setLineDash([5, 5]);
        ctx.strokeStyle = isHovered ? 'rgba(239, 68, 68, 1)' : 'rgba(239, 68, 68, 0.8)';
        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        ctx.quadraticCurveTo(controlX, controlY, to.x, to.y);
        ctx.stroke();
      }

      ctx.restore();
    });
  }, [projectPoint]);

  const getAffectedAirports = useCallback((currentScenario) => {
    if (!currentScenario) return [];

    const affected = new Set();

    if (currentScenario.crisis?.trigger?.route) {
      const route = currentScenario.crisis.trigger.route;
      const [from, to] = route.split('-');
      if (from) affected.add(from);
      if (to) affected.add(to);
    }

    if (currentScenario.crisis?.type === 'flight_cancelled') {
      affected.add('BCN');
      affected.add('LHR');
    } else if (currentScenario.crisis?.type === 'weather') {
      affected.add('AMS');
      affected.add('FRA');
      affected.add('CDG');
    } else if (currentScenario.crisis?.type === 'strike') {
      affected.add('CDG');
      affected.add('ORY');
    }

    return Array.from(affected);
  }, []);

  const drawAirports = useCallback((ctx, nextDimensions, currentScenario, hoveredAirportIata) => {
    const { width, height, offsetX, offsetY } = nextDimensions;
    const affectedAirports = getAffectedAirports(currentScenario);

    AIRPORTS.forEach((airport) => {
      if (!airport.px || !airport.py) return;

      const basePos = scalePixelCoords(airport.px, airport.py, width, height, offsetX, offsetY);
      const pos = projectPoint(basePos.x, basePos.y);
      const isAffected = affectedAirports.includes(airport.iata);
      const isCritical = currentScenario?.crisis?.trigger?.route?.includes(airport.iata);
      const isHovered = hoveredAirportIata === airport.iata;

      let color;
      let glowColor;
      let radius;

      if (isCritical) {
        color = 'rgba(239, 68, 68, 1)';
        glowColor = isHovered ? 'rgba(239, 68, 68, 0.6)' : 'rgba(239, 68, 68, 0.4)';
        radius = isHovered ? 10 : 8;
      } else if (isAffected) {
        color = 'rgba(245, 158, 11, 1)';
        glowColor = isHovered ? 'rgba(245, 158, 11, 0.5)' : 'rgba(245, 158, 11, 0.3)';
        radius = isHovered ? 8 : 6;
      } else {
        color = isHovered ? 'rgba(59, 130, 246, 1)' : 'rgba(59, 130, 246, 0.8)';
        glowColor = isHovered ? 'rgba(59, 130, 246, 0.4)' : 'rgba(59, 130, 246, 0.2)';
        radius = isHovered ? 7 : 5;
      }

      if (isAffected || isCritical || isHovered) {
        ctx.save();
        const gradient = ctx.createRadialGradient(pos.x, pos.y, 0, pos.x, pos.y, radius * 3.2);
        gradient.addColorStop(0, glowColor);
        gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, radius * 3.2, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      ctx.save();
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = isHovered ? 'rgba(255, 255, 255, 1)' : 'rgba(255, 255, 255, 0.8)';
      ctx.lineWidth = isHovered ? 2.5 : 1.5;
      ctx.stroke();
      ctx.restore();

      ctx.save();
      ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
      ctx.font = isHovered ? 'bold 13px Inter, sans-serif' : 'bold 11px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
      ctx.shadowBlur = 3;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 1;
      ctx.fillText(airport.iata, pos.x, pos.y + radius + 4);
      ctx.restore();
    });
  }, [getAffectedAirports, projectPoint]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const { containerWidth, containerHeight } = dimensions;
    const pixelRatio = window.devicePixelRatio || 1;

    canvas.width = Math.round(containerWidth * pixelRatio);
    canvas.height = Math.round(containerHeight * pixelRatio);
    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);

    ctx.clearRect(0, 0, containerWidth, containerHeight);
    drawRoutes(ctx, dimensions, activeRoutes, hoveredRoute);
    drawAirports(ctx, dimensions, scenario, hoveredAirport);
  }, [activeRoutes, dimensions, drawAirports, drawRoutes, hoveredAirport, hoveredRoute, scenario, view]);

  const distanceToQuadraticCurve = useCallback((px, py, x0, y0, cx, cy, x1, y1) => {
    let minDist = Infinity;
    const samples = 50;

    for (let index = 0; index <= samples; index += 1) {
      const t = index / samples;
      const x = (1 - t) * (1 - t) * x0 + 2 * (1 - t) * t * cx + t * t * x1;
      const y = (1 - t) * (1 - t) * y0 + 2 * (1 - t) * t * cy + t * t * y1;
      const dist = Math.sqrt((px - x) ** 2 + (py - y) ** 2);
      if (dist < minDist) {
        minDist = dist;
      }
    }

    return minDist;
  }, []);

  const updateHoverState = useCallback((clientX, clientY) => {
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const localX = clientX - rect.left;
    const localY = clientY - rect.top;
    const worldPoint = unprojectPoint(localX, localY);
    const { width, height, offsetX, offsetY } = dimensions;

    let foundAirport = null;
    for (const airport of AIRPORTS) {
      if (!airport.px || !airport.py) continue;

      const pos = scalePixelCoords(airport.px, airport.py, width, height, offsetX, offsetY);
      const dist = Math.sqrt((worldPoint.x - pos.x) ** 2 + (worldPoint.y - pos.y) ** 2);

      if (dist < 20) {
        foundAirport = airport.iata;
        break;
      }
    }

    if (foundAirport) {
      setHoveredAirport(foundAirport);
      setHoveredRoute(null);
      return;
    }

    let foundRouteIndex = null;
    let minDist = 15;

    activeRoutes.forEach((route, index) => {
      const fromAirport = findAirport(route.from);
      const toAirport = findAirport(route.to);

      if (!fromAirport || !toAirport || !fromAirport.px || !toAirport.px) return;

      const from = scalePixelCoords(fromAirport.px, fromAirport.py, width, height, offsetX, offsetY);
      const to = scalePixelCoords(toAirport.px, toAirport.py, width, height, offsetX, offsetY);
      const { controlX, controlY } = getRouteCurve(from, to);
      const curveDist = distanceToQuadraticCurve(
        worldPoint.x,
        worldPoint.y,
        from.x,
        from.y,
        controlX,
        controlY,
        to.x,
        to.y
      );

      if (curveDist < minDist) {
        minDist = curveDist;
        foundRouteIndex = index;
      }
    });

    setHoveredAirport(null);
    setHoveredRoute(foundRouteIndex);
  }, [activeRoutes, dimensions, distanceToQuadraticCurve, unprojectPoint]);

  const handleWheel = useCallback((event) => {
    event.preventDefault();

    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const localX = event.clientX - rect.left;
    const localY = event.clientY - rect.top;
    const direction = event.deltaY > 0 ? -1 : 1;
    const factor = direction > 0 ? 1 + ZOOM_STEP : 1 - ZOOM_STEP;

    applyZoom(view.scale * factor, localX, localY);
  }, [applyZoom, view.scale]);

  const handlePointerDown = useCallback((event) => {
    if (event.button !== 0) return;

    dragStateRef.current = {
      active: true,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: view.x,
      originY: view.y,
    };

    event.currentTarget.setPointerCapture(event.pointerId);
    setIsDragging(true);
    setHoveredAirport(null);
    setHoveredRoute(null);
  }, [view.x, view.y]);

  const handlePointerMove = useCallback((event) => {
    const dragState = dragStateRef.current;

    if (dragState.active && dragState.pointerId === event.pointerId) {
      const deltaX = event.clientX - dragState.startX;
      const deltaY = event.clientY - dragState.startY;

      setView((prev) => constrainView({
        ...prev,
        x: dragState.originX + deltaX,
        y: dragState.originY + deltaY,
      }));
      return;
    }

    updateHoverState(event.clientX, event.clientY);
  }, [constrainView, updateHoverState]);

  const stopDragging = useCallback((event) => {
    const dragState = dragStateRef.current;
    if (!dragState.active || dragState.pointerId !== event.pointerId) return;

    dragStateRef.current.active = false;
    dragStateRef.current.pointerId = null;

    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    setIsDragging(false);
    updateHoverState(event.clientX, event.clientY);
  }, [updateHoverState]);

  const handlePointerLeave = useCallback(() => {
    if (dragStateRef.current.active) return;
    setHoveredAirport(null);
    setHoveredRoute(null);
  }, []);

  const handleDoubleClick = useCallback((event) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const localX = event.clientX - rect.left;
    const localY = event.clientY - rect.top;
    applyZoom(view.scale * DOUBLE_CLICK_ZOOM, localX, localY);
  }, [applyZoom, view.scale]);

  const zoomFromCenter = useCallback((direction) => {
    const centerX = dimensions.containerWidth / 2;
    const centerY = dimensions.containerHeight / 2;
    const factor = direction > 0 ? 1 + ZOOM_STEP : 1 - ZOOM_STEP;

    applyZoom(view.scale * factor, centerX, centerY);
  }, [applyZoom, dimensions.containerHeight, dimensions.containerWidth, view.scale]);

  const mapTransform = `translate3d(${view.x}px, ${view.y}px, 0) scale(${view.scale})`;

  return (
    <div
      ref={containerRef}
      className="relative h-full w-full overflow-hidden bg-[#07111f]"
      onDoubleClick={handleDoubleClick}
      onPointerDown={handlePointerDown}
      onPointerLeave={handlePointerLeave}
      onPointerMove={handlePointerMove}
      onPointerUp={stopDragging}
      onPointerCancel={stopDragging}
      onWheel={handleWheel}
      style={{
        cursor: isDragging ? 'grabbing' : 'grab',
        touchAction: 'none',
      }}
    >
      <div className="pointer-events-none absolute inset-0">
        <motion.div
          className="absolute inset-0 opacity-80"
          animate={{
            background: [
              'radial-gradient(circle at 18% 22%, rgba(56, 189, 248, 0.18), transparent 34%), radial-gradient(circle at 82% 18%, rgba(251, 191, 36, 0.14), transparent 24%), linear-gradient(180deg, rgba(3, 7, 18, 0.15), rgba(3, 7, 18, 0.7))',
              'radial-gradient(circle at 26% 28%, rgba(56, 189, 248, 0.22), transparent 36%), radial-gradient(circle at 76% 24%, rgba(251, 191, 36, 0.18), transparent 26%), linear-gradient(180deg, rgba(3, 7, 18, 0.08), rgba(3, 7, 18, 0.72))',
              'radial-gradient(circle at 18% 22%, rgba(56, 189, 248, 0.18), transparent 34%), radial-gradient(circle at 82% 18%, rgba(251, 191, 36, 0.14), transparent 24%), linear-gradient(180deg, rgba(3, 7, 18, 0.15), rgba(3, 7, 18, 0.7))',
            ],
          }}
          transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
        />
        <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-cyan-300/8 via-transparent to-transparent" />
        <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-amber-300/8 via-transparent to-transparent" />
      </div>

      <motion.div
        className="pointer-events-none absolute inset-0 will-change-transform"
        animate={{ opacity: 1 }}
        initial={{ opacity: 0.92 }}
        style={{
          transform: mapTransform,
          transformOrigin: '0 0',
        }}
      >
        <img
          ref={imgRef}
          src="/europe-map.png?v=2x"
          alt="Europe Map"
          className="absolute inset-0 h-full w-full object-contain opacity-90"
          style={{ filter: 'brightness(0.72) contrast(1.08) saturate(1.06)' }}
        />
      </motion.div>

      <canvas
        ref={canvasRef}
        width={dimensions.containerWidth || dimensions.width}
        height={dimensions.containerHeight || dimensions.height}
        className="pointer-events-none absolute inset-0 h-full w-full"
        style={{
          width: '100%',
          height: '100%',
        }}
      />

      {weatherEvents.map((event, idx) => {
        const airport = findAirport(event.location);
        if (!airport || !airport.px || !airport.py) return null;

        const basePos = scalePixelCoords(
          airport.px,
          airport.py,
          dimensions.width,
          dimensions.height,
          dimensions.offsetX || 0,
          dimensions.offsetY || 0
        );
        const pos = projectPoint(basePos.x, basePos.y);

        return (
          <motion.div
            key={`${event.location}-${event.type}-${idx}`}
            animate={{
              opacity: [0.7, 1, 0.78],
              y: [-2, 2, -2],
              scale: [0.96, 1.06, 0.96],
            }}
            className="pointer-events-none absolute"
            style={{
              left: `${pos.x}px`,
              top: `${pos.y - 42}px`,
              transform: 'translate(-50%, -50%)'
            }}
            transition={{ duration: 3.4, delay: idx * 0.18, repeat: Infinity, ease: 'easeInOut' }}
          >
            {event.type === 'storm' && (
              <CloudLightning className="h-8 w-8 text-yellow-300 drop-shadow-[0_0_14px_rgba(253,224,71,0.45)]" />
            )}
            {event.type === 'fog' && (
              <Cloud className="h-8 w-8 text-slate-200 drop-shadow-[0_0_14px_rgba(226,232,240,0.35)]" />
            )}
          </motion.div>
        );
      })}

      <div className="pointer-events-none absolute left-5 top-5 z-20 rounded-2xl border border-white/10 bg-slate-950/72 px-4 py-3 text-white shadow-[0_18px_60px_rgba(2,6,23,0.55)] backdrop-blur-xl">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-cyan-200/70">
          <Move className="h-3.5 w-3.5" />
          Ops Airspace
        </div>
        <div className="mt-2 text-lg font-semibold text-white/95">Interactive Situation Map</div>
        <div className="mt-1 text-xs text-slate-300/80">
          Drag to scan routes, wheel to zoom, double-click for a tighter view.
        </div>
      </div>

      <div
        className="absolute right-5 top-5 z-20 flex flex-col gap-2"
        onDoubleClick={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
        onWheel={(event) => event.stopPropagation()}
      >
        <div className="rounded-2xl border border-white/10 bg-slate-950/76 p-1.5 shadow-[0_18px_60px_rgba(2,6,23,0.55)] backdrop-blur-xl">
          <button
            className="flex h-10 w-10 items-center justify-center rounded-xl text-white/80 transition hover:bg-white/10 hover:text-white"
            onClick={() => zoomFromCenter(1)}
            type="button"
          >
            <Plus className="h-4 w-4" />
          </button>
          <button
            className="mt-1 flex h-10 w-10 items-center justify-center rounded-xl text-white/80 transition hover:bg-white/10 hover:text-white"
            onClick={() => zoomFromCenter(-1)}
            type="button"
          >
            <Minus className="h-4 w-4" />
          </button>
          <button
            className="mt-1 flex h-10 w-10 items-center justify-center rounded-xl text-white/80 transition hover:bg-white/10 hover:text-white"
            onClick={resetView}
            type="button"
          >
            <RotateCcw className="h-4 w-4" />
          </button>
        </div>

        <motion.div
          animate={{ opacity: [0.78, 1, 0.82] }}
          className="rounded-2xl border border-cyan-300/20 bg-slate-950/72 px-3 py-2 text-right text-white shadow-[0_18px_60px_rgba(2,6,23,0.45)] backdrop-blur-xl"
          transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut' }}
        >
          <div className="text-[10px] uppercase tracking-[0.24em] text-cyan-200/70">Zoom</div>
          <div className="mt-1 text-lg font-semibold">{Math.round(view.scale * 100)}%</div>
        </motion.div>
      </div>

      <div className="pointer-events-none absolute bottom-5 left-5 z-20 rounded-2xl border border-white/10 bg-slate-950/68 px-4 py-3 text-xs text-slate-200/85 shadow-[0_18px_60px_rgba(2,6,23,0.45)] backdrop-blur-xl">
        Hover airports and routes for details. Critical lines retain visual priority as you zoom.
      </div>

      {hoveredAirport && (() => {
        const airport = findAirport(hoveredAirport);
        if (!airport) return null;

        const basePos = scalePixelCoords(
          airport.px,
          airport.py,
          dimensions.width,
          dimensions.height,
          dimensions.offsetX || 0,
          dimensions.offsetY || 0
        );
        const pos = projectPoint(basePos.x, basePos.y);

        return (
          <div
            className="pointer-events-none absolute z-50 rounded-md bg-black/80 px-3 py-2 text-sm text-white shadow-lg"
            style={{
              left: `${pos.x}px`,
              top: `${pos.y - 50}px`,
              transform: 'translateX(-50%)'
            }}
          >
            <div className="font-semibold">{airport.name}</div>
            <div className="text-xs text-gray-300">{airport.iata}</div>
          </div>
        );
      })()}

      {hoveredRoute !== null && activeRoutes[hoveredRoute] && (() => {
        const route = activeRoutes[hoveredRoute];
        const fromAirport = findAirport(route.from);
        const toAirport = findAirport(route.to);
        if (!fromAirport || !toAirport) return null;

        const from = scalePixelCoords(
          fromAirport.px,
          fromAirport.py,
          dimensions.width,
          dimensions.height,
          dimensions.offsetX || 0,
          dimensions.offsetY || 0
        );
        const to = scalePixelCoords(
          toAirport.px,
          toAirport.py,
          dimensions.width,
          dimensions.height,
          dimensions.offsetX || 0,
          dimensions.offsetY || 0
        );

        const screenMidpoint = projectPoint((from.x + to.x) / 2, (from.y + to.y) / 2);
        const statusLabels = {
          critical: 'Critical',
          cancelled: 'Cancelled',
          delayed: 'Delayed',
          normal: 'Normal'
        };

        return (
          <div
            className="pointer-events-none absolute z-50 rounded-md bg-black/80 px-3 py-2 text-sm text-white shadow-lg"
            style={{
              left: `${screenMidpoint.x}px`,
              top: `${screenMidpoint.y - 30}px`,
              transform: 'translateX(-50%)'
            }}
          >
            <div className="font-semibold">{route.from} → {route.to}</div>
            <div className="text-xs text-gray-300">
              Status: {statusLabels[route.status] || route.status}
            </div>
          </div>
        );
      })()}
    </div>
  );
};
